import { useEffect } from "react";
import { useParams } from "react-router-dom";
import BlankPdfIcon from "../../../assets/icons/BlankPdfIcon";
import PdfIcon from "../../../assets/icons/PdfIcon";
import { APP_NAME, IMAGE_URL } from "../../../constants/constants";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import { singTrainingMaterialAsync } from "../../../store/features/trainingMaterial/trainingMaterialApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { extension } from "../../../utils/array";
import CustomSection from "../../ui/CustomSection";
import IconicTitle from "../../ui/IconicTitle";

type TrainingStatus = "Published" | "CSO-Admin-Approved" | "Draft" | "Archived";

const dealerStatusClass: Record<TrainingStatus, string> = {
  Published: "status-done", // Using status-done for green
  "CSO-Admin-Approved": "status-done",
  Draft: "status-draft", // Using status-draft for gray
  Archived: "status-danger", // Using status-danger for red
};

const changeStatused = (status: TrainingStatus) => {
  // Optional formatter (e.g., replace hyphens with spaces and capitalize)
  return status.replace(/-/g, " ");
};

export default function TrainingMaterialView() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { singleTrainingMaterialData } = useAppSelector(
    (state) => state.trainingMaterial
  );

  useEffect(() => {
    if (id) {
      dispatch(singTrainingMaterialAsync({ params: { id } }));
    }
  }, [dispatch, id]);

  return (
    <AdminLayout
      title="Training Material View"
      backLink="/admin/training/training-material"
    >
      <CustomSection>
        <div className="flex justify-between items-start mb-6">
          <div className="h-8 px-1.5 py-1 bg-[#ffc107] justify-center items-center inline-flex">
            <div className="text-center text-black text-sm font-normal font-['Tiro Bangla'] leading-normal tracking-tight">
              {singleTrainingMaterialData?.categoryId?.categoryName}
            </div>
          </div>

          <span
            className={
              singleTrainingMaterialData?.trainingMaterialStatus
                ? dealerStatusClass[
                    singleTrainingMaterialData.trainingMaterialStatus as TrainingStatus
                  ]
                : ""
            }
          >
            {singleTrainingMaterialData?.trainingMaterialStatus}
          </span>
        </div>

        <div className="text-[#212529] text-5xl font-semibold leading-[60px] mb-6">
          {singleTrainingMaterialData?.trainingMaterialTitle}
        </div>

        <div className="py-6 justify-start items-center inline-flex">
          <div className="justify-start items-center gap-3 flex">
            <div className="flex-col justify-start items-end inline-flex">
              <img
                className="w-14 h-14 rounded-full"
                src={
                  singleTrainingMaterialData?.trainingMaterialAuthor
                    ?.profilePicture
                    ? `${IMAGE_URL}${singleTrainingMaterialData.trainingMaterialAuthor.profilePicture}`
                    : "placeholder-image-url"
                }
                alt={APP_NAME}
              />
            </div>

            <div className="flex-col justify-center items-start gap-1 inline-flex">
              <div className="justify-start items-start gap-1.5 inline-flex">
                <div className="text-[#495057] text-base font-semibold font-['Encode Sans Condensed'] leading-normal">
                  By{" "}
                  {singleTrainingMaterialData?.trainingMaterialAuthor?.fullName}
                </div>
                <div className="text-[#adb5bd] text-sm font-semibold font-['Encode Sans Condensed'] leading-snug">
                  |
                </div>
                <div className="text-[#495057] text-sm font-semibold font-['Encode Sans Condensed'] leading-snug">
                  {singleTrainingMaterialData?.trainingMaterialAuthor
                    ?.designation || "N/A"}
                </div>
              </div>
              <div className="justify-start items-start gap-1.5 inline-flex">
                <div className="text-[#6c757d] text-sm font-medium font-['Encode Sans Condensed'] leading-snug">
                  Last updated:{" "}
                  {formatDate(singleTrainingMaterialData?.updatedAt)}{" "}
                  {formatTime(singleTrainingMaterialData?.updatedAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 border border-[#e9eff6] border-solid" />

        <div className="flex-col justify-start items-start gap-6 inline-flex">
          <img
            className="self-stretch h-[434.88px]"
            loading="lazy"
            src={`${IMAGE_URL}${singleTrainingMaterialData?.trainingMaterialThumbnail}`}
            alt={APP_NAME}
          />
          <div
            className="text-[#343a40] text-base leading-7"
            dangerouslySetInnerHTML={{
              __html: singleTrainingMaterialData?.trainingMaterialContent || "",
            }}
          />
        </div>
      </CustomSection>

      {(singleTrainingMaterialData?.trainingMaterialPdfFiles?.length ?? 0) >
        0 && (
        <CustomSection>
          <IconicTitle
            icon={<BlankPdfIcon />}
            className="text-primary"
            title="PDF File Attachment"
          />

          <div className="grid sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 items-center gap-6 mt-6">
            {singleTrainingMaterialData?.trainingMaterialPdfFiles?.map(
              (file, i) => (
                <div
                  key={i}
                  className="w-[105px] flex-col justify-start items-start gap-2.5 inline-flex"
                >
                  <a
                    href={`${IMAGE_URL}${file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {!["jpg", "jpeg", "png", "gif", "webp"].includes(
                      extension(file).toLowerCase()
                    ) ? (
                      <div className="self-stretch h-[124.50px] rounded-[3px] border border-[#e9eff6] flex items-center justify-center">
                        <PdfIcon />
                      </div>
                    ) : (
                      <img
                        className="self-stretch h-[124.50px] rounded-[3px] border border-[#e9eff6]"
                        src={`${IMAGE_URL}${file}`}
                        alt={APP_NAME}
                      />
                    )}
                    <div className="text-body text-sm font-medium leading-tight whitespace-nowrap">
                      {file?.split("-").pop()}
                    </div>
                  </a>
                </div>
              )
            )}
          </div>
        </CustomSection>
      )}

      {(singleTrainingMaterialData?.trainingMaterialVideoLinks?.length ?? 0) >
        0 && (
        <CustomSection>
          <IconicTitle
            icon={<BlankPdfIcon />}
            className="text-primary"
            title="Video Links"
          />
          <div className="flex flex-col gap-2 mt-4">
            {singleTrainingMaterialData?.trainingMaterialVideoLinks?.map(
              (link, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {link}
                </a>
              )
            )}
          </div>
        </CustomSection>
      )}
    </AdminLayout>
  );
}
